import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Full-screen gift-opening intro. Plays once per browser session.
const HALVES = {
  top: {
    wrapper: "absolute left-0 right-0 top-0 h-1/2 overflow-hidden",
    background: "linear-gradient(180deg, #f6ece1 0%, #e6d6c2 60%, #d8c5a8 100%)",
    exitY: "-105%",
    duration: 1.2,
    delay: 0.45,
    sparkles:
      "radial-gradient(circle at 20% 30%, rgba(139,69,19,0.35) 0 1px, transparent 2px), radial-gradient(circle at 70% 60%, rgba(139,69,19,0.25) 0 1px, transparent 2px), radial-gradient(circle at 40% 80%, rgba(139,69,19,0.3) 0 1px, transparent 2px), radial-gradient(circle at 85% 20%, rgba(139,69,19,0.4) 0 1px, transparent 2px)",
    ribbon: "linear-gradient(180deg, #8B4513 0%, #6e3710 100%)",
    edge: "absolute bottom-0 left-0 right-0 h-3",
    edgeGradient:
      "linear-gradient(180deg, rgba(42,29,21,0.18) 0%, transparent 100%)",
  },
  bottom: {
    wrapper: "absolute left-0 right-0 bottom-0 h-1/2 overflow-hidden",
    background: "linear-gradient(0deg, #f6ece1 0%, #e6d6c2 60%, #d8c5a8 100%)",
    exitY: "105%",
    duration: 1.1,
    delay: 1.0,
    sparkles:
      "radial-gradient(circle at 30% 70%, rgba(139,69,19,0.35) 0 1px, transparent 2px), radial-gradient(circle at 75% 30%, rgba(139,69,19,0.25) 0 1px, transparent 2px), radial-gradient(circle at 50% 50%, rgba(139,69,19,0.3) 0 1px, transparent 2px), radial-gradient(circle at 15% 20%, rgba(139,69,19,0.4) 0 1px, transparent 2px)",
    ribbon: "linear-gradient(180deg, #6e3710 0%, #8B4513 100%)",
    edge: "absolute top-0 left-0 right-0 h-3",
    edgeGradient:
      "linear-gradient(0deg, rgba(42,29,21,0.2) 0%, transparent 100%)",
  },
};

function useIntroVisibility() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("gc_intro_played");
  });

  useEffect(() => {
    if (!visible) return;
    // Lock scroll while playing
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const t = setTimeout(() => {
      sessionStorage.setItem("gc_intro_played", "1");
      setVisible(false);
    }, 2600);

    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return visible;
}

function BoxHalf({ position }) {
  const cfg = HALVES[position];
  return (
    <motion.div
      initial={{ y: 0 }}
      animate={{ y: cfg.exitY }}
      transition={{ duration: cfg.duration, delay: cfg.delay, ease: [0.7, 0, 0.3, 1] }}
      className={cfg.wrapper}
      style={{ background: cfg.background }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-20 mix-blend-multiply"
        style={{ backgroundImage: cfg.sparkles, backgroundSize: "240px 240px" }}
      />
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.25, delay: 0.35 }}
        className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[60px] md:w-[80px]"
        style={{
          background: cfg.ribbon,
          boxShadow: "0 0 30px rgba(42,29,21,0.45) inset",
        }}
      />
      <div
        aria-hidden
        className={cfg.edge}
        style={{ background: cfg.edgeGradient }}
      />
    </motion.div>
  );
}

function MiddleRibbon() {
  return (
    <motion.div
      initial={{ scaleX: 1, opacity: 1 }}
      animate={{ scaleX: 0, opacity: 0 }}
      transition={{ duration: 0.55, delay: 0.85, ease: [0.7, 0, 0.3, 1] }}
      style={{
        transformOrigin: "center center",
        background:
          "linear-gradient(90deg, #6e3710 0%, #8B4513 50%, #6e3710 100%)",
        boxShadow: "0 6px 18px rgba(42,29,21,0.35)",
      }}
      className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[40px] md:h-[60px] z-10"
    />
  );
}

function Bow() {
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-20"
      style={{ top: "calc(50% - 150px)" }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], scale: [0.85, 1, 1, 1.1], y: [10, 0, 0, -8] }}
        transition={{ duration: 2.0, times: [0, 0.18, 0.6, 1], ease: "easeOut" }}
      >
        <svg
          viewBox="0 0 120 60"
          className="w-32 md:w-44 h-auto drop-shadow-[0_12px_24px_rgba(42,29,21,0.55)]"
          aria-hidden
        >
          <defs>
            <linearGradient id="bow-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8B4513" />
              <stop offset="100%" stopColor="#5a2c0c" />
            </linearGradient>
          </defs>
          <path
            d="M60 30 C 30 5, 10 5, 5 25 C 0 45, 30 55, 60 30 Z"
            fill="url(#bow-grad)"
          />
          <path
            d="M60 30 C 90 5, 110 5, 115 25 C 120 45, 90 55, 60 30 Z"
            fill="url(#bow-grad)"
          />
          <ellipse cx="60" cy="30" rx="9" ry="11" fill="#f6ece1" />
        </svg>
      </motion.div>
    </div>
  );
}

function GiftTag() {
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-20"
      style={{ top: "calc(50% + 60px)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.95 }}
        animate={{
          opacity: [0, 1, 1, 0],
          y: [24, 0, 0, -10],
          scale: [0.95, 1, 1, 1.06],
        }}
        transition={{ duration: 2.1, times: [0, 0.22, 0.62, 1], ease: "easeOut" }}
        className="relative bg-[#fbf1ea] px-10 py-6 md:px-14 md:py-8 shadow-[0_30px_60px_-22px_rgba(42,29,21,0.55)]"
        style={{
          background: "linear-gradient(180deg, #fdfbf7 0%, #f6ece1 100%)",
        }}
      >
        <span
          aria-hidden
          className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#2A1D15]/85"
        />
        <div className="flex flex-col items-center gap-3">
          <span className="block w-10 h-px bg-[#8B4513]/55" />
          <p className="font-serif-display text-[#2A1D15] text-3xl md:text-5xl lg:text-6xl tracking-[-0.01em] text-center leading-none whitespace-nowrap">
            Wrapped <span className="italic text-[#8B4513]">with love</span>.
          </p>
          <span className="block w-10 h-px bg-[#8B4513]/55" />
        </div>
      </motion.div>
    </div>
  );
}

export default function GiftOpening() {
  const visible = useIntroVisibility();

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
          data-testid="intro-overlay"
        >
          <BoxHalf position="top" />
          <BoxHalf position="bottom" />
          <MiddleRibbon />
          <Bow />
          <GiftTag />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
